import React, { useState } from 'react';
import Modal, { ModalCloseButton } from './Modal';
import ConfirmDialog from './ConfirmDialog';
import StatusPicker from './StatusPicker';
import StatusHistory from './StatusHistory';
import TermineSection from './TermineSection';
import { apiFetch } from '../api';
import { getCurrentPosition, SAVE_ERROR_MSG, CONFLICT_ERROR_MSG } from '../utils';
import { enqueueOfflineSave } from '../offlineQueue';
import { celebrateClose } from '../celebration';
import { CONFLICT_REFRESHED_MSG, fetchFreshLead, newerLead } from './leadConflict';

const API = '/api/leads';

export default function DetailModal({ lead: leadProp, selectedUnit, onClose, onSave, onDelete, onOpenUnits, authHeader, user }) {
  // Nach einem 409 nachgeladener Stand — ab dann Basis für Anzeige und Speichern
  const [freshLead, setFreshLead] = useState(null);
  const lead = newerLead(freshLead, leadProp);
  const [conflict, setConflict] = useState(false);
  // Bei MFH wird der Gebäude-Status serverseitig aus den Einheiten abgeleitet —
  // hier nur anzeigen, gepflegt wird er im Einheiten-Picker
  const isMultiUnit = (lead.anzahl_ne ?? 0) >= 2;
  const [notizen, setNotizen] = useState(lead.notizen || '');
  const [vertriebStatus, setVertriebStatus] = useState(lead.vertrieb_status ?? null);
  const [vertriebStatusCount, setVertriebStatusCount] = useState(lead.vertrieb_status_count ?? 1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Ziel-Status, der einen bestehenden Abschluss aufheben würde — erst nach Rückfrage
  // übernehmen (undefined = keine Rückfrage offen; null = "Nicht bearbeitet" ist ein gültiges Ziel)
  const [pendingStatus, setPendingStatus] = useState(undefined);

  const applyStatus = v => { setVertriebStatus(v); setVertriebStatusCount(1); };
  const selectStatus = v => {
    if (vertriebStatus === 'Geclosed' && v !== 'Geclosed') { setPendingStatus(v); return; }
    applyStatus(v);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setConflict(false);
    // GPS nur beim ersten Abschluss erfassen — außerhalb des try, damit sie bei
    // einem Netzwerkfehler auch im Offline-Queue-Fallback (catch) erhalten bleibt
    let geoData = {};
    const isNowClosed = vertriebStatus === 'Geclosed';
    const wasAlreadyClosed = !!lead.geclosed_by;
    if (isNowClosed && !wasAlreadyClosed) {
      const pos = await getCurrentPosition();
      geoData = {
        geclosed_by:  user?.username ?? null,
        geclosed_lat: pos?.lat ?? null,
        geclosed_lng: pos?.lng ?? null,
      };
    }
    const body = { ...lead, notizen, vertrieb_status: vertriebStatus, vertrieb_status_count: vertriebStatusCount, ...geoData };
    try {
      const res = await apiFetch(`${API}/${lead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify(body),
      });
      if (res.status === 409) {
        const fresh = await fetchFreshLead(lead.id, authHeader);
        if (!fresh) { setError(CONFLICT_ERROR_MSG); return; }
        // Nicht angefasste Felder auf den neuen Stand ziehen, eigene Eingaben behalten
        if (notizen === (lead.notizen || '')) setNotizen(fresh.notizen || '');
        if (vertriebStatus === (lead.vertrieb_status ?? null) && vertriebStatusCount === (lead.vertrieb_status_count ?? 1)) {
          setVertriebStatus(fresh.vertrieb_status ?? null);
          setVertriebStatusCount(fresh.vertrieb_status_count ?? 1);
        }
        setFreshLead(fresh);
        setConflict(true);
        setError(CONFLICT_REFRESHED_MSG);
        return;
      }
      if (!res.ok) throw new Error(`Fehler ${res.status}`);
      if (isNowClosed && !wasAlreadyClosed) celebrateClose();
      onSave(await res.json());
    } catch (err) {
      // fetch() wirft TypeError nur bei echtem Netzwerkausfall (kein Server erreichbar) —
      // dann lokal übernehmen und automatisch synchronisieren, sobald wieder Verbindung da ist.
      if (err instanceof TypeError) {
        enqueueOfflineSave(user?.username, lead.id, body);
        if (isNowClosed && !wasAlreadyClosed) celebrateClose();
        onSave({ ...body, _pendingSync: true });
        return;
      }
      console.error('Fehler beim Speichern:', err);
      setError(SAVE_ERROR_MSG);
    } finally {
      setSaving(false);
    }
  };

  const addr = [lead.strasse, lead.hausnummer, lead.zusatz].filter(Boolean).join(' ');
  const fullAddr = [addr, lead.plz, lead.ort].filter(Boolean).join(', ');

  return (
    <Modal onClose={onClose} label={`Details — ${fullAddr}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h2>{fullAddr}</h2>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{lead.ha_id} · {lead.gebaeude_typ} · {lead.anzahl_ne} NE</span>
          </div>
          <ModalCloseButton onClose={onClose} />
        </div>
        {selectedUnit != null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#ede9fe', borderRadius: 8, padding: '6px 14px', marginBottom: 14, fontSize: 13, color: '#4c1d95', fontWeight: 600 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            Einheit NE {selectedUnit}
          </div>
        )}

        {/* Termin anlegen / einsehen — über dem Status */}
        <TermineSection leadId={lead.id} authHeader={authHeader} />

        {/* Status setzen — bei MFH über die Einheiten */}
        {isMultiUnit ? (
          <div style={{ marginBottom: 14, padding: '10px 14px', background: '#f5f3ff', border: '1px solid #ede9fe', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180, fontSize: 13, color: '#4c1d95' }}>
              <strong>Gebäude-Status: {lead.vertrieb_status || 'Nicht bearbeitet'}</strong>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>ergibt sich aus den {lead.anzahl_ne} Einheiten</div>
            </div>
            <button type="button" className="btn btn-primary" onClick={onOpenUnits}>Einheiten bearbeiten</button>
          </div>
        ) : (
          <div style={{ marginBottom: 4 }}>
            <div className="section-label">Status setzen</div>
            <StatusPicker
              current={vertriebStatus}
              count={vertriebStatusCount}
              onSelect={selectStatus}
              onCountIncrement={() => setVertriebStatusCount(c => {
                // Pro Speicherung höchstens +1 über dem gespeicherten Stand (Server erzwingt das ebenfalls)
                const max = vertriebStatus === (lead.vertrieb_status ?? null) ? (lead.vertrieb_status_count ?? 1) + 1 : 1;
                return Math.min(c + 1, max);
              })}
            />
            <StatusHistory leadId={lead.id} authHeader={authHeader} />
          </div>
        )}

        {/* Infos */}
        <div className="detail-grid">
          <div className="detail-item"><span>Kategorie</span><strong>{lead.kategorie}</strong></div>
          <div className="detail-item"><span>Internet</span><strong>{lead.internet_verfuegbar}</strong></div>
          <div className="detail-item"><span>GEE liegt vor</span><strong>{lead.gee_liegt_vor}</strong></div>
          <div className="detail-item"><span>SV-Potenzial</span><strong>{lead.sv_potenzial}</strong></div>
          {lead.mfh_eigentuemer && <div className="detail-item full"><span>Eigentümer</span><strong>{lead.mfh_eigentuemer}</strong></div>}
          {lead.kundennamen_sv && <div className="detail-item full"><span>Kunden mit SV</span><strong>{lead.kundennamen_sv}</strong></div>}
          {lead.rabattcode && <div className="detail-item"><span>Rabattcode</span><strong>{lead.rabattcode}</strong></div>}
          {lead.rabattcode_gueltig_bis && <div className="detail-item"><span>Gültig bis</span><strong>{lead.rabattcode_gueltig_bis}</strong></div>}
          {lead.montage_termin && <div className="detail-item full"><span>Montagetermin</span><strong>{lead.montage_termin}</strong></div>}
        </div>

        <div className="form-field" style={{ marginTop: 14 }}>
          <label>Notizen</label>
          <textarea rows={3} value={notizen} onChange={e => setNotizen(e.target.value)} placeholder="Eigene Notizen..." />
        </div>

        {error && (
          <div className="login-error" role="alert" style={{ marginTop: 12 }}>{error}</div>
        )}

        <div className="form-actions">
          {user?.role === 'admin' && (
            <button type="button" className="btn btn-danger" onClick={() => onDelete(lead)}>Löschen</button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button
            type="button"
            className={`btn btn-primary${saving ? ' btn-loading' : ''}`}
            onClick={handleSave}
            disabled={saving}
            aria-busy={saving}
          >
            {saving ? 'Speichern...' : conflict ? 'Erneut speichern' : error ? 'Erneut versuchen' : 'Speichern'}
          </button>
        </div>
        {pendingStatus !== undefined && (
          <ConfirmDialog
            title="Abschluss aufheben?"
            message={`Der Status wird auf „${pendingStatus ?? 'Nicht bearbeitet'}“ gesetzt. Datum, Standort und Zuordnung des Abschlusses gehen dabei verloren und er zählt nicht mehr in der Statistik — der Verlauf bleibt erhalten.`}
            confirmLabel="Abschluss aufheben"
            onConfirm={() => { applyStatus(pendingStatus); setPendingStatus(undefined); }}
            onCancel={() => setPendingStatus(undefined)}
          />
        )}
    </Modal>
  );
}
